import { Component, EventEmitter, Input, OnChanges, Output, inject, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormGroup, ValidationErrors } from "@angular/forms";
import { TranslocoService } from "@jsverse/transloco";
import { DateAdapter } from "@angular/material/core";

import { LorenEntityFormModel } from "../../../loren-entity-form-card/interfaces/loren-entity-form-model.interface.ts";
import { FormFieldInteraction } from "../../../loren-entity-form-card/components/loren-entity-form-card/interfaces/form-field-interaction.interface";
import { LorenFormFieldError } from "../../../loren-entity-form-card/interfaces/loren-form-field-error.interface";
import { LorenFormSubmission } from "../../../loren-entity-form-card/interfaces/loren-form-submission.interface";
import { LorenFormBuilderService } from "../../services/loren-form-builder-service/loren-form-builder.service";
import { LorenFormErrorsDecoratorService } from "../../services/loren-form-errors-decorator-service/loren-form-errors-decorator.service";
import { LorenBreakpointObserverService } from "../../../../services/loren-breakpoint-observer-service/loren-breakpoint-observer.service";

@Component({
    selector: "loren-entity-form",
    templateUrl: "./loren-entity-form.component.html",
    styleUrls: ["./loren-entity-form.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LorenEntityFormComponent implements OnChanges {
  private formBuilderService = inject(LorenFormBuilderService);
  private lorenFormErrorsDecoratorService = inject(LorenFormErrorsDecoratorService);
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private breakpointObserverService = inject(LorenBreakpointObserverService);
  private translateService = inject(TranslocoService);

  @Input() formModel: LorenEntityFormModel;
  @Input() handleErrors: boolean;
  @Input() showErrors: boolean;
  @Input() validateNonDirtyOnSubmit: boolean;
  @Input() fullLengthFields: boolean;

  @Output() submitForm = new EventEmitter<LorenFormSubmission>();
  @Output() formFieldInteraction = new EventEmitter<FormFieldInteraction>();
  public form: UntypedFormGroup;
  formErrors: string[] = [];
  public isHandset$ =
    this.breakpointObserverService.getHandsetStateSubscription();

  constructor() {
    this.dateAdapter.setLocale(this.translateService.getActiveLang());
  }

  ngOnChanges(): void {
    this.buildFormGroup();
  }

  onSubmit() {
    const formSubmission: LorenFormSubmission = {
      valid: !this.form.invalid,
      dirty: this.form.dirty,
      value: this.form.value,
    };

    if (this.handleErrors) {
      this.formErrors = [];
      formSubmission.errors = this.getFormErrors(this.form);
      this.lorenFormErrorsDecoratorService.decorateErrors(
        formSubmission.errors
      );
      this.formErrors = formSubmission.errors.map((x) => x.errMsg);
    } else {
      if (this.validateNonDirtyOnSubmit && this.form.invalid) {
        Object.keys(this.form.controls).forEach((field) => {
          const control = this.form.get(field);
          control.markAsTouched({ onlySelf: true });
        });
      }
    }

    this.submitForm.emit(formSubmission);
  }

  getFormErrors(form: UntypedFormGroup) {
    const errors: LorenFormFieldError[] = [];
    if (form.invalid) {
      const namesToLabels = new Map(
        this.formModel.formFields.map((x) => [x.name, x.label])
      );
      Object.keys(form.controls).forEach((key) => {
        const controlErrors: ValidationErrors = form.get(key)?.errors;
        if (controlErrors != null) {
          Object.keys(controlErrors).forEach((keyError) => {
            errors.push({
              control: key,
              label: namesToLabels.get(key),
              errKey: keyError,
              errVal: controlErrors[keyError],
            });
          });
        }
      });
    }

    return errors;
  }

  handleFormFieldInteraction(interaction: FormFieldInteraction) {
    this.formFieldInteraction.emit(interaction);
    // if (this.formModel.formFieldConditions && this.formModel.formFieldConditions.length > 0) {
    //     this.formModel.formFieldConditions.forEach(condition => {
    //         this.handleFormFieldCondition(condition, interaction);
    //     });
    // }
  }

  private buildFormGroup() {
    this.form = this.formBuilderService.buildFormGroup(this.formModel);
  }

  //
  // private handleFormFieldCondition(condition: LorenFormFieldCondition, interaction: FormFieldInteraction) {
  //     if (interaction.fieldName !== condition.fieldName) {
  //         return;
  //     }
  //
  //     const fieldValue = interaction.fieldCurrentValue;
  //     const modifyField = this.formModel.formFields.find(x => x.name === condition.modifyField);
  //
  //     if (fieldValue === condition.equalsTo) {
  //         modifyField[condition.modifyProperty] = condition.modifyValue;
  //         this.form.removeControl(condition.modifyField);
  //         // const control = new FormControl({
  //         //     disabled: condition.modifyValue
  //         // });
  //         // this.form.addControl(condition.modifyField, control);
  //     }
  // }
}
